import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Send, Globe } from 'lucide-react';
import { useLang } from '../context/LangContext';

import { normalizeCmsImages } from '../api/config';
import { resolveMediaUrl } from '../utils/media';
import { cmsApi, settingsApi } from '../api/requests';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [settings, setSettings] = useState({ telegramUrl: '', instagramUrl: '' });
  const [cms, setCms] = useState(null);
  const location = useLocation();
  const { t, lang, setLang, LANGUAGES } = useLang();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    Promise.all([
      settingsApi.getPublic(),
      cmsApi.getSection('header')
    ]).then(([s, c]) => { setSettings(s.data); setCms(normalizeCmsImages(c.data)); })
      .catch(() => {});
  }, []);

  useEffect(() => { setIsMenuOpen(false); setIsLangOpen(false); }, [location]);

  const logoText = cms?.logoText || 'CREO';
  const logoAccent = cms?.logoAccent || 'FARM';
  const navLinks = [
    { id: 'home', label: t('nav.home'), path: '/' },
    { id: 'collection', label: t('nav.collection'), path: '/catalog' },
    { id: 'custom-order', label: t('nav.customOrder'), path: '/custom-order' },
    { id: 'about', label: t('nav.about'), path: '/#about' },
    { id: 'faq', label: t('nav.faq'), path: '/#faq' },
  ];
  const showSocial = cms?.showSocialIcons !== false;
  const currentLang = LANGUAGES.find(l => l.code === lang);

  return (
    <header data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'glass border-b border-white/10' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center space-x-2">
            {cms?.logoImage
              ? <img src={resolveMediaUrl(cms.logoImage)} alt={logoText} className="h-8 md:h-10" />
              : <span className="text-xl md:text-2xl font-bold tracking-tight text-white font-['Outfit']">{logoText}<span className="text-indigo-400">{logoAccent}</span></span>}
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map(link => (
              <Link key={link.id} to={link.path} data-testid={`nav-${link.id}`}
                className={`text-sm font-medium transition-colors hover:text-indigo-400 ${location.pathname === link.path ? 'text-indigo-400' : 'text-zinc-300'}`}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center space-x-3">
            {/* Language switcher */}
            <div className="relative">
              <button onClick={() => setIsLangOpen(!isLangOpen)} data-testid="lang-switcher"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-zinc-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-full transition-colors">
                <Globe className="w-3.5 h-3.5" />
                <span>{currentLang?.label}</span>
              </button>
              {isLangOpen && (
                <div className="absolute right-0 top-full mt-2 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-xl min-w-[120px]">
                  {LANGUAGES.map(l => (
                    <button key={l.code} onClick={() => { setLang(l.code); setIsLangOpen(false); }}
                      data-testid={`lang-${l.code}`}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${lang === l.code ? 'bg-indigo-500/15 text-indigo-400' : 'text-zinc-300 hover:bg-white/5 hover:text-white'}`}>
                      <span>{l.flag}</span><span>{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {showSocial && settings.telegramUrl && (
              <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="header-telegram"
                className="p-2 text-zinc-400 hover:text-indigo-400 transition-colors" aria-label="Telegram"><Send className="w-5 h-5" /></a>
            )}
            {showSocial && settings.instagramUrl && (
              <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="header-instagram"
                className="p-2 text-zinc-400 hover:text-indigo-400 transition-colors" aria-label="Instagram"><Instagram className="w-5 h-5" /></a>
            )}
            <Link to="/catalog" data-testid="header-browse-btn"
              className="px-5 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium rounded-full transition-all hover:scale-105">
              {t('nav.browseCollection')}
            </Link>
          </div>

          <button onClick={() => setIsMenuOpen(!isMenuOpen)} data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-zinc-300 hover:text-white">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden glass border-t border-white/10">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map(link => (
              <Link key={link.id} to={link.path} data-testid={`mobile-nav-${link.id}`}
                className={`block text-lg font-medium transition-colors ${location.pathname === link.path ? 'text-indigo-400' : 'text-zinc-300 hover:text-white'}`}>
                {link.label}
              </Link>
            ))}
            {/* Mobile language switcher */}
            <div className="flex items-center gap-2 pt-4 border-t border-white/10">
              <Globe className="w-4 h-4 text-zinc-400" />
              {LANGUAGES.map(l => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`mobile-lang-${l.code}`}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${lang === l.code ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'text-zinc-400 hover:text-white'}`}>
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
            {showSocial && (
              <div className="flex items-center space-x-4 pt-4 border-t border-white/10">
                {settings.telegramUrl && <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-zinc-300 hover:text-indigo-400"><Send className="w-5 h-5" /><span>Telegram</span></a>}
                {settings.instagramUrl && <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-zinc-300 hover:text-indigo-400"><Instagram className="w-5 h-5" /><span>Instagram</span></a>}
              </div>
            )}
            <Link to="/catalog" data-testid="mobile-browse-btn"
              className="block w-full text-center px-5 py-3 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-full">
              {t('nav.browseCollection')}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
